import { adjustedEbitda } from "@/lib/finance";
import { formatCompactMoney } from "@/lib/format";
import type { Adjustment, AdjustmentDirection, CaseAnalysis, Cents } from "@/lib/types";
import { readCase, writeCase } from "./store";
import { appendActivity } from "./activity";

export async function getAnalysis(caseId: string): Promise<CaseAnalysis | null> {
  return (await readCase(caseId))?.analysis ?? null;
}

export interface AdjustmentInput {
  label: string;
  direction: AdjustmentDirection;
  amount: Cents;
  reason: string;
}

let sequence = 0;

/**
 * Adds an analyst adjustment to the EBITDA bridge.
 *
 * Every adjustment carries user, timestamp, reason and the before/after it
 * produced. That is the spec's requirement and it is enforced here rather than
 * left to the caller — an adjustment without a reason never reaches the store.
 */
export async function addAdjustment(
  caseId: string,
  input: AdjustmentInput,
  actor: string,
): Promise<CaseAnalysis | null> {
  const reason = input.reason.trim();
  const label = input.label.trim();
  if (label === "" || reason === "" || input.amount <= 0) {
    throw new Error("An adjustment needs a label, a positive amount and a reason.");
  }

  sequence += 1;
  const adjustment: Adjustment = {
    id: `adj-live-${sequence}`,
    label,
    direction: input.direction,
    amount: input.amount,
    reason,
    user: actor,
    at: new Date().toISOString(),
  };

  let before: Cents | null = null;
  let after: Cents | null = null;

  await writeCase(caseId, (record) => {
    before = adjustedEbitda(record.analysis.bridge);
    record.analysis.bridge.adjustments.push(adjustment);
    after = adjustedEbitda(record.analysis.bridge);
  });

  if (before !== null && after !== null) {
    await appendActivity(caseId, {
      actor,
      kind: "adjustment",
      title: "EBITDA adjusted",
      from: formatCompactMoney(before),
      to: formatCompactMoney(after),
      reason: `${label} — ${reason}`,
    });
  }

  return getAnalysis(caseId);
}

export async function removeAdjustment(
  caseId: string,
  adjustmentId: string,
  actor: string,
): Promise<CaseAnalysis | null> {
  let removed: Adjustment | null = null;
  let before: Cents | null = null;
  let after: Cents | null = null;

  await writeCase(caseId, (record) => {
    const index = record.analysis.bridge.adjustments.findIndex((a) => a.id === adjustmentId);
    if (index === -1) return;
    before = adjustedEbitda(record.analysis.bridge);
    removed = record.analysis.bridge.adjustments.splice(index, 1)[0] ?? null;
    after = adjustedEbitda(record.analysis.bridge);
  });

  if (removed && before !== null && after !== null) {
    await appendActivity(caseId, {
      actor,
      kind: "adjustment",
      title: "EBITDA adjustment removed",
      from: formatCompactMoney(before),
      to: formatCompactMoney(after),
      reason: (removed as Adjustment).label,
    });
  }

  return getAnalysis(caseId);
}
